import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import InfoItem from "../../components/InfoItem";
import DatePicker from "../../components/DatePicker";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import { managerService } from "../../../../services/api/managerService";
import { firebaseService } from "../../../../services/api/firebaseService";
import { AuthContext } from "../../../../contexts/AuthContext";
import { toast } from "react-toastify";
import { formatDate, formatDateInput } from "../../../../utils/DateFormat";
import Loading from "../../components/Loading";
import Modal from "../../components/Modal";
import MobilePatternHeader from "../../components/MobilePatternHeader";
import Button from "../../components/Button";
import BackButton from "../../components/BackButton";
import { ReactComponent as TrashCanIcons } from "../../../../assets/icons/trash-can.svg";
import { ReactComponent as EditIcons } from "../../../../assets/icons/edit.svg";
import { StatusBadge } from "../../../../constants/statusConstants";

/* ================= Constants ================= */
const FALLBACK_AVATAR = "/images/avatar-default.png";

const GENDER_OPTIONS = [
  { value: "Nam", label: "Nam" },
  { value: "Nữ", label: "Nữ" },
  { value: "Khác", label: "Khác" },
];

const STATUS_OPTIONS = [
  { value: "Đang hoạt động", label: "Đang hoạt động" },
  { value: "Không hoạt động", label: "Không hoạt động" },
];

const ROLE_OPTIONS = [
  { value: "Staff", label: "Nhân viên" },
  { value: "Collaborator", label: "Cộng tác viên" },
];

/* ================= Helpers ================= */
const roleLabelVN = (raw) => {
  const name = typeof raw === "string" ? raw : raw?.name;
  if (name === "Manager") return "Quản lý";
  if (name === "Staff") return "Nhân viên";
  return "Cộng tác viên";
};

const toOption = (val, options, fallback = null) =>
  options.find((o) => o.value === val) || fallback || options[0];

const formatStaff = (resp = {}) => {
  const addr = resp.address || {};
  return {
    avatar: resp.avatar || resp.avatarUrl || FALLBACK_AVATAR,
    fullName: resp.fullName || "",
    dob: resp.dateOfBirth ? formatDate(resp.dateOfBirth) : "",
    gender: resp.gender || "",
    email: resp.email || "",
    phone: resp.phone || "",
    city: addr.city || "",
    district: addr.district || "",
    address: addr.detail || "",
    employeeId: resp.staffCode || "",
    username: resp.username || "",
    passwordMask: "********",
    joinDate: resp.joinDate ? formatDate(resp.joinDate) : "",
    roleLabel: roleLabelVN(resp.role),
    status: resp.status || "--",
  };
};

const normalizeForEdit = (res) => {
  const r = res || {};
  return {
    avatarUrl: r.avatarUrl || "",
    fullName: r.fullName || "",
    birth: formatDateInput(r.dateOfBirth) || "",
    gender: toOption(r.gender, GENDER_OPTIONS, {
      value: "Khác",
      label: "Khác",
    }),
    email: r.email || "",
    phone: r.phone || "",
    city: r.address?.city || "",
    district: r.address?.district || "",
    address: r.address?.detail || "",
    employeeCode: r.staffCode || "",
    username: r.username || "",
    password: "",
    joinDate: formatDateInput(r.joinDate) || "",
    status: toOption(r.status, STATUS_OPTIONS, STATUS_OPTIONS[0]),
    role: toOption(r.role?.name, ROLE_OPTIONS, ROLE_OPTIONS[0]),
  };
};

/* ================= Component ================= */
const UserInfo = ({ isMobile, mode = "view" }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token_client } = useContext(AuthContext);

  const [isEditMode, setIsEditMode] = useState(mode === "edit");
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageSrc, setImageSrc] = useState(FALLBACK_AVATAR);
  const [imageFile, setImageFile] = useState(null);
  const [error, setError] = useState({});
  const [openPicker, setOpenPicker] = useState(null);

  // Modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isMountedRef = useRef(true);
  const submittingRef = useRef(false);
  const abortRef = useRef(null);

  useEffect(() => {
    setIsEditMode(mode === "edit");
  }, [mode]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort?.();
    };
  }, []);

  const fetchUserData = useCallback(async () => {
    if (!id) return;

    abortRef.current?.abort?.();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      const res = await managerService.get_staff_by_id(
        id,
        token_client,
        controller.signal,
      );

      if (!isMountedRef.current) return;

      setViewData(formatStaff(res?.data));
      setEditData(normalizeForEdit(res?.data));
      setImageSrc(res?.data?.avatarUrl || FALLBACK_AVATAR);
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.error(error);
      toast.dismiss();
      toast.error(
        error?.response?.data?.message ||
        "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [id, token_client]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleEdit = () => {
    setIsEditMode(true);
    navigate(`/tai-khoan-nhan-vien/nhan-vien/${id}/chinh-sua`);
    setError({});
    setImageFile(null);
  };

  const confirmCancelEdit = useCallback(() => {
    setIsEditMode(false);
    navigate(`/tai-khoan-nhan-vien/nhan-vien/${id}`);
    if (viewData) {
      setEditData(normalizeForEdit(viewData));
      setImageSrc(viewData.avatar || FALLBACK_AVATAR);
    }
    setImageFile(null);
    setError({});
    setShowCancelModal(false);
  }, [id, navigate, viewData]);

  const handleCancelEdit = () => {
    setShowCancelModal(true);
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      await managerService.delete_staff(id, token_client);
      toast.success("Xóa tài khoản thành công.");
      navigate("/tai-khoan-nhan-vien");
    } catch (error) {
      console.error(error);
      toast.dismiss();
      toast.error(
        error?.response?.data?.message ||
        "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleDatePickerToggle = (picker) =>
    setOpenPicker((prev) => (prev === picker ? null : picker));

  const handleSelectChange = (name, option) =>
    setEditData((prev) => ({ ...prev, [name]: option }));

  const handleChange = (field, value) =>
    setEditData((prev) => ({ ...prev, [field]: value }));

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const okType = ["image/jpeg", "image/png"].includes(file.type);
    if (!okType) {
      toast.dismiss();
      toast.error("Chỉ chấp nhận định dạng JPEG/PNG.");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.dismiss();
      toast.error("Kích thước tệp vượt quá 25MB.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setImageSrc(reader.result || FALLBACK_AVATAR);
    reader.onerror = () => toast.error("Không thể đọc tệp. Vui lòng thử lại.");
    reader.readAsDataURL(file);
    setImageFile(file);
  };

  const validate = () => {
    const e = {};
    const required = ["fullName", "email", "phone", "username"];

    required.forEach((k) => {
      if (
        !editData[k] ||
        (typeof editData[k] === "string" && !editData[k].trim())
      ) {
        e[k] = "Trường này không được để trống.";
      }
    });

    if (editData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editData.email)) {
      e.email = "Email không hợp lệ.";
    }
    if (!/^(03|05|07|08|09)\d{8}$/.test(editData.phone || "")) {
      e.phone = "Số điện thoại không hợp lệ.";
    }
    if (editData.password && editData.password.length < 6) {
      e.password = "Mật khẩu phải lớn hơn 6 kí tự";
    }

    setError(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!validate()) return;

    try {
      submittingRef.current = true;
      setLoading(true);

      let finalAvatarUrl = editData.avatarUrl;

      if (imageFile instanceof File) {
        const uploadRes = await firebaseService.upload_file(
          token_client,
          imageFile,
        );

        if (!uploadRes?.url) {
          toast.error("Upload avatar thất bại");
          return;
        }

        finalAvatarUrl = uploadRes.url;

        if (editData.avatarUrl && editData.avatarUrl.startsWith("https://")) {
          try {
            await firebaseService.delete_file(token_client, editData.avatarUrl);
          } catch (err) {
            console.warn("Delete old avatar failed:", err);
          }
        }
      }

      const payload = {
        fullName: editData.fullName,
        dateOfBirth: formatDateInput(editData.birth),
        gender: editData.gender.value,
        email: editData.email,
        phone: editData.phone,
        address: {
          city: editData.city,
          district: editData.district,
          detail: editData.address,
        },
        staffCode: editData.employeeCode,
        username: editData.username,
        joinDate: formatDateInput(editData.joinDate),
        role: editData.role.value,
        status: editData.status.value,
        avatarUrl: finalAvatarUrl,
      };

      if (editData.password?.trim()) {
        payload.password = editData.password;
      }

      await managerService.update_user(payload, id, token_client);
      toast.success("Cập nhật thông tin nhân viên thành công!");

      await fetchUserData();
      setIsEditMode(false);
      setImageFile(null);
      setError({});
    } catch (err) {
      toast.dismiss();
      toast.error(
        err?.response?.data?.message ||
        "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
    } finally {
      submittingRef.current = false;
      if (isMountedRef.current) setLoading(false);
    }
  };

  if (loading && !viewData) {
    return (
      <div
        className={`flex ${isMobile ? "h-screen w-screen" : "h-full"
          } items-center justify-center`}
      >
        <Loading />
      </div>
    );
  }

  if (!viewData && !isEditMode) {
    return (
      <div className="p-4 text-gray-600">Không có dữ liệu để hiển thị.</div>
    );
  }

  /* =============== MOBILE EDIT MODE =============== */
  if (isMobile && isEditMode && editData) {
    return (
      <div className="h-full w-full">
        <Modal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          title="Xác nhận hủy"
          maxWidth="420px"
          showCancelButton
          cancelButtonText="Tiếp tục chỉnh sửa"
          primaryButtonText="Hủy chỉnh sửa"
          primaryButtonVariant="danger"
          primaryButtonOnClick={confirmCancelEdit}
        >
          <div className="px-6 py-4">
            <p className="text-gray-700">
              Bạn có chắc chắn muốn hủy chỉnh sửa không?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Các thay đổi chưa lưu sẽ bị mất.
            </p>
          </div>
        </Modal>

        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Xác nhận xóa"
          maxWidth="420px"
          showCancelButton
          cancelButtonText="Hủy"
          primaryButtonText={isDeleting ? "Đang xóa..." : "Xóa nhân viên"}
          primaryButtonVariant="danger"
          primaryButtonOnClick={confirmDelete}
          primaryButtonDisabled={isDeleting}
        >
          <div className="px-6 py-4">
            <p className="text-gray-700">
              Bạn chắc chắn muốn xoá nhân viên này?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Thao tác này không thể hoàn tác.
            </p>
          </div>
        </Modal>

        <MobilePatternHeader
          title="Chỉnh sửa thông tin nhân viên"
          onBack={handleCancelEdit}
        />

        <div className="h-[calc(100%-64px)] bg-[#F2F2F2] p-3">
          <div className="h-[calc(100%-70px)] w-full overflow-auto">
            <div className="w-full flex flex-col items-center mt-1">
              <label
                htmlFor="image-upload"
                className="cursor-pointer size-[100px] rounded-full bg-white flex items-center justify-center overflow-hidden relative hover:opacity-60"
              >
                <img
                  src={imageSrc}
                  alt="Avatar"
                  className="size-[100px] object-cover rounded-full"
                />
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  autoComplete="off"
                  onChange={handleImageChange}
                />
              </label>
              <p className="text-[14px] font-sf-medium text-[#0267FF] mt-1">
                Tải ảnh lên
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-[14px] font-sf-medium text-[#0267FF] pb-1 border-b border-[#E1E5F3]">
                Thông tin chung
              </p>

              <CustomInput
                placeholder="Nhập họ tên"
                name="fullName"
                required
                value={editData.fullName}
                onChange={(v) => handleChange("fullName", v)}
                error={error.fullName}
              />

              <div className="grid grid-cols-2 gap-3">
                <DatePicker
                  placeholder="Ngày sinh"
                  initValue={editData.birth}
                  onChange={(v) => handleChange("birth", v)}
                  height="52px"
                  isCalendarOpen={openPicker === "birthDate"}
                  onToggle={() => handleDatePickerToggle("birthDate")}
                />
                <CustomSelect
                  height="52px"
                  required
                  options={GENDER_OPTIONS}
                  placeholder="Giới tính"
                  value={editData.gender}
                  onChange={(opt) => handleSelectChange("gender", opt)}
                  className="bg-white"
                  error={error.gender}
                />

                <DatePicker
                  placeholder="Ngày vào làm"
                  initValue={editData.joinDate}
                  onChange={(v) => handleChange("joinDate", v)}
                  height="52px"
                  isCalendarOpen={openPicker === "joinDate"}
                  onToggle={() => handleDatePickerToggle("joinDate")}
                />
                <CustomInput
                  placeholder="Số điện thoại"
                  name="phone"
                  required
                  value={editData.phone}
                  error={error.phone}
                  onChange={(v) => handleChange("phone", v)}
                />
              </div>

              <CustomInput
                placeholder="Email"
                name="email"
                value={editData.email}
                required
                type="email"
                error={error.email}
                onChange={(v) => handleChange("email", v)}
              />

              <div className="grid grid-cols-2 gap-3">
                <CustomInput
                  placeholder="Tỉnh/Thành phố"
                  name="city"
                  value={editData.city}
                  onChange={(v) => handleChange("city", v)}
                />
                <CustomInput
                  placeholder="Quận/Huyện"
                  name="district"
                  value={editData.district}
                  onChange={(v) => handleChange("district", v)}
                />
              </div>

              <CustomInput
                placeholder="Địa chỉ"
                name="address"
                value={editData.address}
                onChange={(v) => handleChange("address", v)}
              />
            </div>

            <div className="space-y-3 mt-3 pb-10vh">
              <p className="text-[14px] font-sf-medium text-[#0267FF] pb-1 border-b border-[#E1E5F3]">
                Thông tin tài khoản
              </p>

              <CustomInput
                placeholder="Tên tài khoản"
                name="username"
                required
                value={editData.username}
                error={error.username}
                onChange={(v) => handleChange("username", v)}
              />

              <div className="grid grid-cols-2 gap-3">
                <CustomInput
                  placeholder="Mã số nhân viên"
                  name="employeeCode"
                  value={editData.employeeCode}
                  disabled
                  onChange={(v) => handleChange("employeeCode", v)}
                />
                <CustomInput
                  placeholder="Mật khẩu mới"
                  name="password"
                  value={editData.password}
                  type="password"
                  error={error.password}
                  onChange={(v) => handleChange("password", v)}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <CustomSelect
                  options={STATUS_OPTIONS}
                  placeholder="Trạng thái"
                  value={editData.status}
                  onChange={(opt) => handleSelectChange("status", opt)}
                  height="52px"
                  required
                  className="bg-white"
                />
                <CustomSelect
                  options={ROLE_OPTIONS}
                  placeholder="Phân quyền"
                  value={editData.role}
                  onChange={(opt) => handleSelectChange("role", opt)}
                  height="52px"
                  className="bg-white"
                />
              </div>
            </div>

            <div className="flex justify-end my-6 gap-4 w-full">
              <Button
                type="button"
                onClick={handleCancelEdit}
                variant="secondary"
                size="lg"
                className="w-full"
              >
                Hủy
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submittingRef.current || loading}
                isLoading={submittingRef.current || loading}
                variant="primary"
                size="lg"
                className="w-full"
              >
                {submittingRef.current || loading ? "Đang lưu..." : "Lưu"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEditMode && !editData) {
    return (
      <div
        className={`flex ${isMobile ? "h-screen w-screen" : "h-full"
          } items-center justify-center`}
      >
        <Loading />
      </div>
    );
  }

  /* =============== MOBILE VIEW MODE =============== */
  if (isMobile) {
    return (
      <div className="w-screen h-screen pt-[90px] relative">
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Xác nhận xóa"
          maxWidth="420px"
          showCancelButton
          cancelButtonText="Hủy"
          primaryButtonText={isDeleting ? "Đang xóa..." : "Xóa nhân viên"}
          primaryButtonVariant="danger"
          primaryButtonOnClick={confirmDelete}
          primaryButtonDisabled={isDeleting}
        >
          <div className="px-6 py-4">
            <p className="text-gray-700">
              Bạn chắc chắn muốn xoá nhân viên này?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Thao tác này không thể hoàn tác.
            </p>
          </div>
        </Modal>

        <div className="w-full h-full bg-white rounded-2xl p-3">
          <div className="flex items-center justify-center flex-col">
            <h1 className="mt-8 text-[#0267FF] text-[20px] font-sf-semibold">
              {viewData.username || viewData.fullName}
            </h1>
            <p className="opacity-70">{viewData.roleLabel}</p>
          </div>

          <div className="overflow-auto h-[calc(100%-210px)]">
            <div>
              <p className="font-sf-medium">Thông tin chung</p>
              <div className="drop-shadow-md shadow rounded-md p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <p>Ngày sinh:</p>
                  <p>{viewData.dob || "--"}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Giới tính:</p>
                  <p>{viewData.gender || "--"}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Số điện thoại:</p>
                  <p>{viewData.phone || "--"}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Email:</p>
                  <p>{viewData.email || "--"}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Địa chỉ:</p>
                  <p className="text-right">
                    {[
                      viewData.address || "--",
                      viewData.district || "--",
                      viewData.city || "--",
                    ].join(", ")}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-3">
              <p className="font-sf-medium">Thông tin tài khoản</p>
              <div className="drop-shadow-md shadow rounded-md p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <p>Mã số nhân viên:</p>
                  <p>{viewData.employeeId || "--"}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Tên tài khoản:</p>
                  <p>{viewData.username || "--"}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Mật khẩu:</p>
                  <p>{viewData.passwordMask}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Phân quyền:</p>
                  <p>{viewData.roleLabel}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Ngày tạo:</p>
                  <p>{viewData.joinDate || "--"}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Trạng thái:</p>
                  <StatusBadge
                    status={viewData.status}
                    className="min-w-[96px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute top-[30px] flex w-full justify-center">
          <img
            src={viewData.avatar || FALLBACK_AVATAR}
            alt="User avatar"
            className="rounded-full size-[100px] object-cover"
          />
        </div>

        <div className="w-full p-3 absolute bottom-[85px] left-0 flex items-center gap-3">
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={handleEdit}
            className="w-full"
          >
            Chỉnh sửa
          </Button>
          <Button
            type="button"
            variant="danger"
            size="lg"
            onClick={handleDelete}
            className="w-full"
          >
            Xoá nhân viên
          </Button>
        </div>
      </div>
    );
  }

  /* =============== DESKTOP EDIT MODE =============== */
  if (isEditMode && editData) {
    return (
      <div className="h-full overflow-auto">
        <Modal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          title="Xác nhận hủy"
          maxWidth="420px"
          showCancelButton
          cancelButtonText="Tiếp tục chỉnh sửa"
          primaryButtonText="Hủy chỉnh sửa"
          primaryButtonVariant="danger"
          primaryButtonOnClick={confirmCancelEdit}
        >
          <div className="px-6 py-4">
            <p className="text-gray-700">
              Bạn có chắc chắn muốn hủy chỉnh sửa không?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Các thay đổi chưa lưu sẽ bị mất.
            </p>
          </div>
        </Modal>

        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Xác nhận xóa"
          maxWidth="420px"
          showCancelButton
          cancelButtonText="Hủy"
          primaryButtonText={isDeleting ? "Đang xóa..." : "Xóa nhân viên"}
          primaryButtonVariant="danger"
          primaryButtonOnClick={confirmDelete}
          primaryButtonDisabled={isDeleting}
        >
          <div className="px-6 py-4">
            <p className="text-gray-700">
              Bạn chắc chắn muốn xoá nhân viên này?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Thao tác này không thể hoàn tác.
            </p>
          </div>
        </Modal>

        <div className="bg-white p-6 px-[5vw] lap:px-[10vw] 2xl:px-[15vw] rounded-lg">
          <div>
            <div className="flex items-start gap-2 mb-4">
              <img src="/icons/fi-user.svg" alt="User Icons" />
              <h2>Thông tin chung</h2>
            </div>

            <div className="flex items-center">
              <label
                htmlFor="image-upload"
                className="cursor-pointer hover:opacity-80"
              >
                <img
                  src={imageSrc || FALLBACK_AVATAR}
                  alt="Avatar"
                  className="w-28 h-28 rounded-lg object-cover object-center"
                />
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  autoComplete="off"
                  onChange={handleImageChange}
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4 my-4">
              <CustomInput
                placeholder="Nhập họ tên"
                name="fullName"
                value={editData.fullName}
                onChange={(v) => handleChange("fullName", v)}
                error={error.fullName}
              />
              <CustomSelect
                options={GENDER_OPTIONS}
                placeholder="Giới tính"
                value={editData.gender}
                onChange={(opt) => handleSelectChange("gender", opt)}
                height="52px"
                width="100%"
                className="bg-white"
              />
              <div className="flex items-start justify-between gap-4">
                <DatePicker
                  placeholder="Ngày sinh"
                  initValue={editData.birth}
                  onChange={(v) => handleChange("birth", v)}
                  height="52px"
                  isCalendarOpen={openPicker === "birthDate"}
                  onToggle={() => handleDatePickerToggle("birthDate")}
                />
              </div>
              <CustomInput
                placeholder="Email"
                name="email"
                value={editData.email}
                type="email"
                onChange={(v) => handleChange("email", v)}
                error={error.email}
              />
              <CustomInput
                placeholder="Số điện thoại"
                name="phone"
                value={editData.phone}
                onChange={(v) => handleChange("phone", v)}
                error={error.phone}
              />
              <div className="flex items-start justify-between gap-4">
                <DatePicker
                  placeholder="Ngày vào làm"
                  initValue={editData.joinDate}
                  onChange={(v) => handleChange("joinDate", v)}
                  height="52px"
                  isCalendarOpen={openPicker === "joinDate"}
                  onToggle={() => handleDatePickerToggle("joinDate")}
                />
              </div>
              <CustomInput
                placeholder="Tỉnh/Thành phố"
                name="city"
                value={editData.city}
                onChange={(v) => handleChange("city", v)}
              />
              <CustomInput
                placeholder="Quận/Huyện"
                name="district"
                value={editData.district}
                onChange={(v) => handleChange("district", v)}
              />
              <div className="col-span-2">
                <CustomInput
                  placeholder="Địa chỉ"
                  name="address"
                  value={editData.address}
                  onChange={(v) => handleChange("address", v)}
                />
              </div>
            </div>

            <div className="flex items-start gap-2 my-6">
              <img src="/icons/bag.svg" alt="User Icons" />
              <h2>Thông tin tài khoản</h2>
            </div>
            <div className="grid grid-cols-3 gap-4 my-4">
              <CustomInput
                placeholder="Mã số nhân viên"
                name="employeeCode"
                disabled={true}
                value={editData.employeeCode}
                onChange={(v) => handleChange("employeeCode", v)}
              />
              <CustomInput
                placeholder="Tên tài khoản"
                name="username"
                value={editData.username}
                onChange={(v) => handleChange("username", v)}
                error={error.username}
              />
              <CustomInput
                type="password"
                placeholder="Nhập mật khẩu mới"
                value={editData.password}
                onChange={(v) => handleChange("password", v)}
                error={error.password}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 my-4">
              <CustomSelect
                options={STATUS_OPTIONS}
                placeholder="Trạng thái"
                value={editData.status}
                onChange={(opt) => handleSelectChange("status", opt)}
                height="52px"
                width="100%"
                required={true}
              />
              <CustomSelect
                options={ROLE_OPTIONS}
                placeholder="Phân quyền"
                value={editData.role}
                onChange={(opt) => handleSelectChange("role", opt)}
                height="52px"
                width="100%"
                className="bg-white"
              />
            </div>
            <div className="flex justify-end mt-6 gap-4">
              <Button
                type="button"
                onClick={handleCancelEdit}
                variant="secondary"
                size="lg"
              >
                Hủy
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submittingRef.current || loading}
                isLoading={submittingRef.current || loading}
                variant="primary"
                size="lg"
              >
                {submittingRef.current || loading
                  ? "Đang lưu..."
                  : "Lưu thay đổi"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* =============== DESKTOP VIEW MODE =============== */
  return (
    <div className="h-full overflow-auto">
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Xác nhận xóa"
        maxWidth="420px"
        showCancelButton
        cancelButtonText="Hủy"
        primaryButtonText={isDeleting ? "Đang xóa..." : "Xóa nhân viên"}
        primaryButtonVariant="danger"
        primaryButtonOnClick={confirmDelete}
        primaryButtonDisabled={isDeleting}
      >
        <div className="px-6 py-4">
          <p className="text-gray-700">Bạn chắc chắn muốn xoá nhân viên này?</p>
          <p className="text-sm text-gray-500 mt-2">
            Thao tác này không thể hoàn tác.
          </p>
        </div>
      </Modal>

      <div className="bg-white rounded-lg p-6 border-b">
        <div className="flex items-center mb-6 w-full border-b px-[15vw] py-4">
          <img src="/icons/user.svg" alt="user icon" />
          <h2>Thông tin chung</h2>
        </div>

        <div className="space-y-6 px-[15vw]">
          <div className="flex justify-between items-center border-b">
            <div className="flex items-center space-x-6 pb-6">
              <img
                src={viewData.avatar || FALLBACK_AVATAR}
                alt="Avatar"
                className="w-[94px] h-[94px] rounded-lg object-cover"
              />
              <div className="space-y-3">
                <h3 className="text-xl font-semibold">
                  {viewData.fullName || "--"}
                </h3>
                <div className="text-gray-600 flex items-start gap-2">
                  <img src="/icons/fi-user.svg" alt="User icon" />
                  {viewData.roleLabel}
                </div>
                <div className="text-gray-600 flex items-start gap-2">
                  <img src="/icons/mail.svg" alt="Mail icon" />
                  {viewData.email || "--"}
                </div>
              </div>
            </div>
            <StatusBadge status={viewData.status} className="min-w-[120px]" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <InfoItem label="Họ tên" value={viewData.fullName || "--"} />
            <div className="flex gap-4 w-full">
              <InfoItem
                label="Ngày sinh"
                value={viewData.dob || "--"}
                className="w-1/2"
              />
              <InfoItem
                label="Giới tính"
                value={viewData.gender || "--"}
                className="w-1/2"
              />
            </div>
            <InfoItem label="Email" value={viewData.email || "--"} />
            <div className="flex gap-4 w-full">
              <InfoItem
                label="Ngày vào làm"
                value={viewData.joinDate || "--"}
                className="w-1/2"
              />
              <InfoItem
                label="Số điện thoại"
                value={viewData.phone || "--"}
                className="w-1/2"
              />
            </div>
            <InfoItem label="Tỉnh/Thành phố" value={viewData.city || "--"} />
            <InfoItem label="Quận/Huyện" value={viewData.district || "--"} />
            <InfoItem
              label="Địa chỉ:"
              value={viewData.address || "--"}
              className="col-span-2"
            />
          </div>

          <div>
            <div className="flex gap-2 mb-4">
              <img src="/icons/bag.svg" alt="Bag icon" />
              Thông tin tài khoản
            </div>
            <div className="grid grid-cols-3 gap-4">
              <InfoItem
                label="Mã số nhân viên"
                value={viewData.employeeId || "--"}
              />
              <InfoItem
                label="Tên tài khoản"
                value={viewData.username || "--"}
              />
              <InfoItem label="Mật khẩu" value={viewData.passwordMask} />
              <InfoItem
                label="Phân quyền"
                value={viewData.roleLabel}
                className="col-span-3"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-4">
          <BackButton
            label="Quay lại"
            onClick={() => navigate("/tai-khoan-nhan-vien")}
          />
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={handleEdit}
            leftIcon={<EditIcons />}
          >
            Chỉnh sửa
          </Button>
          <Button
            type="button"
            variant="danger"
            size="lg"
            onClick={handleDelete}
            leftIcon={<TrashCanIcons />}
          >
            Xoá nhân viên
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserInfo;
